import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/DashboardLayout';
import ChatPage from './pages/ChatPage';
import SessionsPage from './pages/SessionsPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import ComparisonPage from './pages/ComparisonPage';
import ExecutiveSummaryPage from './pages/ExecutiveSummaryPage';
import { useAuthStore } from './stores/authStore';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, user } = useAuthStore();
  const location = useLocation();

  if (!token || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuthStore();

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  const { token, loadUser } = useAuthStore();

  useEffect(() => {
    // Check for stored token on mount
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');

    if (storedToken && storedUser) {
      useAuthStore.getState().setToken(storedToken);
      useAuthStore.getState().setUser(JSON.parse(storedUser));
      loadUser();
    }
  }, [loadUser]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard/chat" replace />} />
          <Route path="dashboard/chat" element={<ChatPage />} />
          <Route path="dashboard/sessions" element={<SessionsPage />} />
          <Route path="dashboard/reports" element={<ReportsPage />} />
          <Route path="dashboard/comparison" element={<ComparisonPage />} />
          <Route path="dashboard/executive-summary" element={<ExecutiveSummaryPage />} />
          <Route path="dashboard/settings" element={<SettingsPage />} />
        </Route>

        {/* Catch all - redirect to login or dashboard */}
        <Route
          path="*"
          element={
            token ? (
              <Navigate to="/dashboard/chat" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </Box>
  );
}

export default App;
